import { Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ViewOnlyBadge } from 'components/blocks/view-only-badge';
import type { AgentSummary } from 'features/chat/agent-chat-module/types/agent.service.type';
import { cn } from 'lib/utils';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { AgentDetailsHeaderActions } from './agent-details-header-actions';
import { AgentDetailsNavHeader } from './agent-details-nav-header';
import { AgentDetailsTabBar, type AgentDetailsTabItem } from './agent-details-tab-bar';

type AgentDetailsTopBarProps = {
  agentName: string;
  agents?: AgentSummary[];
  tabs: AgentDetailsTabItem[];
  activeTabHref: string;
  isLoading?: boolean;
  isPublishing?: boolean;
  onBack: () => void;
  onAgentSelect: (agentId: string) => void;
  onTabClick: (href: string) => void;
  onCancel: () => void;
  onSaveAndPublish: () => void;
  createdBy?: string | null;
  /** Owner can share / delete / publish. */
  isOwner?: boolean;
  /** Owner or shared editor — can save config. */
  canEdit?: boolean;
  /** True when write access came from a share, not from org-admin rights. */
  isSharedWithMe?: boolean;
  isEmptyWidgetId?: boolean;
  isMyOrganization?: boolean;
  className?: string;
};

export function AgentDetailsTopBar({
  agentName,
  agents = [],
  tabs,
  activeTabHref,
  isLoading = false,
  isPublishing = false,
  onBack,
  onAgentSelect,
  onTabClick,
  onCancel,
  onSaveAndPublish,
  createdBy,
  isOwner = false,
  canEdit = false,
  isSharedWithMe = false,
  isEmptyWidgetId = false,
  isMyOrganization = false,
  className,
}: AgentDetailsTopBarProps) {
  const { t } = useTranslation();
  const userId = useCommonStore((state) => state.user?.id);
  // Avoid flashing callouts before we know both `createdBy` and the current user id.
  const isOwnershipPending = isLoading || createdBy === undefined || userId == null;

  // An org admin editing a colleague's agent was not "shared with" them.
  const nonOwnerEditNote = isSharedWithMe
    ? t('SHARED_WITH_YOU_CAN_EDIT', { defaultValue: 'Shared with you · You can edit' })
    : t('YOU_CAN_EDIT_AS_ORG_ADMIN', {
        defaultValue: 'You can edit as an organisation admin',
      });

  const headerActions = (
    <AgentDetailsHeaderActions
      onCancel={onCancel}
      onSaveAndPublish={onSaveAndPublish}
      isOwner={isOwner}
      isPublishing={isPublishing}
      disabled={isLoading}
    />
  );

  // Only editors can generate a widget ID, so the hint wraps their Save button.
  const showWidgetBanner = !isOwnershipPending && canEdit && isEmptyWidgetId && isMyOrganization;
  // Bold the action name inside the sentence; falls back to plain text if the
  // translation doesn't contain it.
  const widgetBannerText = t('WIDGET_ID_NOT_CREATED_YET', {
    defaultValue: 'Widget ID has not been created yet. Click Save to generate a widget ID.',
  });
  const widgetBannerAction = t('SAVE', { defaultValue: 'Save' });
  const [bannerBefore, bannerAfter] = widgetBannerText.split(widgetBannerAction);
  const widgetBannerClassName =
    'flex items-center gap-3 rounded-lg border border-[#FFE27A] bg-[#FFFAEB] dark:border-amber-600 dark:bg-amber-900/20';
  const widgetBannerMessage = (
    <div className="flex items-center gap-2">
      <Info className="size-4 shrink-0 text-[#725111] dark:text-amber-300" aria-hidden />
      <p className="text-sm leading-[160%] text-[#725111] lg:whitespace-nowrap dark:text-amber-200">
        {bannerAfter === undefined ? (
          widgetBannerText
        ) : (
          <>
            {bannerBefore}
            <strong className="font-semibold">{widgetBannerAction}</strong>
            {bannerAfter}
          </>
        )}
      </p>
    </div>
  );

  const rightDesktop = isOwnershipPending ? (
    <div className="h-11 w-full" aria-hidden />
  ) : canEdit ? (
    <div className="flex flex-col items-end gap-1.5">
      {!isOwner ? (
        <p className="max-w-[240px] text-right text-xs text-muted-foreground">{nonOwnerEditNote}</p>
      ) : null}
      {showWidgetBanner ? (
        <div
          data-testid="empty-widget-id-banner"
          role="status"
          className={cn(widgetBannerClassName, 'py-2 pl-4 pr-2')}
        >
          {widgetBannerMessage}
          {headerActions}
        </div>
      ) : (
        headerActions
      )}
    </div>
  ) : (
    <ViewOnlyBadge
      className="max-w-[240px]"
      description={t('YOU_ARE_NOT_THE_OWNER_OF_THIS_AGENT', {
        defaultValue: 'You are not the owner of this agent.',
      })}
    />
  );

  const rightMobile = isOwnershipPending ? (
    <div className="h-10 w-full max-w-[240px] shrink-0" aria-hidden />
  ) : canEdit ? (
    <div className="flex shrink-0 flex-col items-end gap-1.5">
      {!isOwner ? (
        <p className="max-w-[200px] text-right text-xs text-muted-foreground sm:max-w-[240px]">
          {nonOwnerEditNote}
        </p>
      ) : null}
      {headerActions}
    </div>
  ) : (
    <ViewOnlyBadge
      className="max-w-[200px] sm:max-w-[240px]"
      description={t('YOU_ARE_NOT_THE_OWNER_OF_THIS_AGENT', {
        defaultValue: 'You are not the owner of this agent.',
      })}
    />
  );

  return (
    <div className={cn('bg-background', className)}>
      <div className="hidden min-h-[4.5rem] w-full items-center gap-3 px-4 lg:flex xl:gap-6 xl:px-6">
        <div className="shrink-0">
          <AgentDetailsNavHeader
            agentName={agentName}
            agents={agents}
            isLoading={isLoading}
            onAgentSelect={onAgentSelect}
            onBack={onBack}
          />
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-center overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <AgentDetailsTabBar
            tabs={tabs}
            activeHref={activeTabHref}
            onTabClick={onTabClick}
            disabled={isLoading}
            variant="inline"
            showDivider
          />
        </div>

        <div className="flex min-w-[12rem] shrink-0 justify-end xl:min-w-[17.5rem]">
          {rightDesktop}
        </div>
      </div>

      <div className="lg:hidden">
        <div className="flex min-h-[3.5rem] items-center justify-between gap-2 px-4 py-3 sm:gap-3 sm:px-6">
          <AgentDetailsNavHeader
            agentName={agentName}
            agents={agents}
            isLoading={isLoading}
            onAgentSelect={onAgentSelect}
            onBack={onBack}
            className="min-w-0 flex-1 overflow-hidden"
          />
          {rightMobile}
        </div>
        {showWidgetBanner ? (
          <div className="px-4 pb-3 sm:px-6">
            <div
              data-testid="empty-widget-id-banner"
              role="status"
              className={cn(widgetBannerClassName, 'px-4 py-3')}
            >
              {widgetBannerMessage}
            </div>
          </div>
        ) : null}
        <AgentDetailsTabBar
          tabs={tabs}
          activeHref={activeTabHref}
          onTabClick={onTabClick}
          disabled={isLoading}
          variant="fullWidth"
          className="w-full"
          tabsClassName="px-0"
        />
      </div>
    </div>
  );
}
